import { useEffect, useRef, useState } from 'react';
import StackVertical from '../Wrappers/StackVertical';

type TabsType = {
	tabEntries: Array<TabEntryType>;
	activeIndex?: number;
	vertical?: boolean;
	buttons?: boolean;
};

type TabEntryType = {
	title: string;
	description?: string;
	icon?: any;
	content: any;
	disabled?: boolean;
};

type TabNavigationEntryType = {
	title: string;
	description?: string;
	icon?: any;
	onChange?: any;
	active?: boolean;
	disabled?: boolean;
};

type TabNavigationType = {
	data: Array<TabEntryType>;
	handleChangeCallback: (index: number) => void;
	detailed?: boolean;
	vertical?: boolean;
	activeIndex: number;
};

const TabNavigation = (props: TabNavigationType) => {
	const [indicatorStyle, setIndicatorStyle] = useState<any>({
		width: 100 / props.data.length + '%',
		transform: `translateX(0px)`,
	});

	const [lastIndex, setLastIndex] = useState<number>(props.activeIndex);

	const HandleChangeCallback = (e: any, index: number) => {
		props.handleChangeCallback(index);
		setIndicatorStyle(IndicatorStyle(e.currentTarget, props.vertical));
		setLastIndex(index);
	};

	useEffect(() => {
		if (props.activeIndex !== lastIndex) {
			setIndicatorStyle({
				width: 100 / props.data.length + '%',
				transform: `translateX(0px)`,
			});
		}
	}, [props.activeIndex, lastIndex]);

	return (
		<div className={`tab-navigation ${props.detailed ? 'tab-navigation-detailed' : ''}`}>
			{props.data.map((tab, index: number) => (
				<TabNavigationEntry
					key={`tabNavigation${index}`}
					onChange={(e: any) => HandleChangeCallback(e, index)}
					title={tab.title}
					description={tab.description}
					icon={tab.icon}
					active={index === props.activeIndex}
					disabled={tab.disabled}
				/>
			))}
			<div className={'tab-navigation-indicator'} style={indicatorStyle} />
		</div>
	);
};

const TabNavigationEntry = (props: TabNavigationEntryType) => {
	if (props.icon) {
		return (
			<div
				onClick={(e) => props.onChange(e)}
				className={`tab-navigation-entry ${props.active ? 'active' : ''} ${props.disabled ? 'disabled' : ''}`}
			>
				<div className={'tab-navigation-entry-icon'}>{props.icon}</div>
				<StackVertical>
					<div className={'tab-navigation-entry-title'}>{props.title}</div>
					{props.description && <div className={'tab-navigation-entry-description'}>{props.description}</div>}
				</StackVertical>
			</div>
		);
	}

	return (
		<div
			onClick={(e) => props.onChange(e)}
			className={`tab-navigation-entry tab-navigation-entry-simple ${props.active ? 'active' : ''} ${
				props.disabled ? 'disabled' : ''
			}`}
		>
			{props.title}
		</div>
	);
};

const TabContent = (props: any) => {
	if (!props.active) return null;

	return <div className={`tab-content ${props.active ? 'active' : ''}`}>{props.content}</div>;
};

const Tabs = (props: TabsType) => {
	const [activeTabIndex, setActiveTabIndex] = useState<number>(props.activeIndex ?? 0);

	const HandleTabChange = (index: number) => {
		setActiveTabIndex(index);
	};

	return (
		<div className={`tabs ${props.buttons ? 'button-tabs' : ''} ${props.vertical ? 'vertical-tabs' : ''}`}>
			<TabNavigation
				data={props.tabEntries}
				activeIndex={activeTabIndex}
				vertical={props.vertical}
				handleChangeCallback={(e: number) => HandleTabChange(e)}
			/>

			{props.tabEntries.map((tab, index) => (
				<TabContent key={`tabContent${index}`} content={tab.content} active={index === activeTabIndex} />
			))}
		</div>
	);
};

export { TabNavigation };

export default Tabs;

const IndicatorStyle = (el?: any, vertical?: boolean) => {
	if (vertical) {
		return {
			height: el?.offsetHeight ?? 0,
			transform: `translateY(${el?.offsetTop ?? 0}px)`,
		};
	}
	return {
		width: el?.offsetWidth ?? 0,
		transform: `translateX(${el?.offsetLeft ?? 0}px)`,
	};
};
